import { Controller, useForm } from "react-hook-form";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  TextField
} from "@mui/material";
import {
  BodyMedium,
  H6HeadlineSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
  StyledToggleButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { maxBy } from "lodash";
import {
  StyledFormControl,
  StyledOutlinedInput,
  StyledTextField,
  StyledSelect,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import { StyledMenuItem } from "../../../components/StyledComponents/Menu/Menu.tsx";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { useMutation } from "@tanstack/react-query";
import { usePostNewSite } from "../../queries/usePostNewSite.ts";
import { useGetSiteLists } from "../../../api/queries/useGetSiteLists.ts";
import { useAuth0 } from "@auth0/auth0-react";
import { usePostNewDG } from "../../queries/usePostNewDG.ts";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

const SiteOrGroupSelectionFormHeader = ({
  setOpen,
  formReset,
  setActiveStep,
}) => {
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0"}
    >
      <H6HeadlineSmall>
        What would you like to add?
      </H6HeadlineSmall>

      <StyledIconButton 
        colors={colors}
        onClick={() => {
          setOpen(false);
          formReset();
          setActiveStep(0);
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const SiteAndGroupNamingFormHeader = ({
  setOpen,
  formReset,
  siteConfig,
  setActiveStep,
}) => {
  const { whichForm } = siteConfig;
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0"}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
      >
        <H6HeadlineSmall>
          {whichForm === "addSite"
            ? "Add a site"
            : "Add a group"}
        </H6HeadlineSmall>
        {whichForm === "addSite" && (
          <BodyMedium>
            Ensure the site name matches the one used by
            your Q.S.E. If you do not have a Q.S.E., you may
            disregard this.
          </BodyMedium>
        )}
        {whichForm === "addGroup" && (
          <BodyMedium>
            Enter a name and select the site you would like
            to assign this group to.
          </BodyMedium>
        )}
      </Box>

      <StyledIconButton   
        colors={colors}
        onClick={() => {
          setOpen(false);
          formReset();
          setActiveStep(0);
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};
const SiteOrGroupSelectionForm = ({
  onMoveForward,
  setOpen,
  setActiveStep,
  role,
  siteListsLength,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset } =
    useForm({
      defaultValues: {
        whichForm: "",
      },
    });

  console.log("siteListsLength", siteListsLength);

  const formToShow = watch("whichForm");

  const onSubmit = (data) => {
    const finalData = { ...data };
    onMoveForward(finalData);
  };
  return (
    <>
      <SiteOrGroupSelectionFormHeader
        formReset={reset}
        setOpen={setOpen}
        setActiveStep={setActiveStep}
      />
      <form onSubmit={handleSubmit(onMoveForward)}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"flex-start"}
          flexGrow={1}
          padding={0}
          gap={6}
        >
          <Box display={"flex"} flexDirection={"row"}>
            <StyledFormControl colors={colors}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 4,
                margin: 0,
              }}
            >
              <Controller
                name="whichForm"
                control={control}
                defaultValue={null}
                render={({
                  field: { value, onChange },
                }) => (
                  <StyledToggleButton
                    colors={colors}
                    disabled={
                      siteListsLength >= 16 ||
                      role !== "superadmin"
                    }
                    value={"addSite"}
                    onClick={() =>
                      onChange(
                        formToShow === "addSite"
                          ? null
                          : "addSite"
                      )
                    }
                    selected={formToShow === "addSite"}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                  >
                    {formToShow === "addSite" ? (
                      <CheckIcon fontSize={"small"} />
                    ) : (
                      ""
                    )}{" "}
                    Site
                  </StyledToggleButton>
                )}
              />
              <Controller
                name="whichForm"
                control={control}
                defaultValue={null}
                render={({
                  field: { value, onChange },
                }) => (
                  <StyledToggleButton
                    colors={colors}
                    disabled={
                      role !== "superadmin" &&
                      role !== "pooladmin" &&
                      role !== "admin"
                    }
                    value={"addGroup"}
                    onClick={() =>
                      onChange(
                        formToShow === "addGroup"
                          ? null
                          : "addGroup"
                      )
                    }
                    selected={formToShow === "addGroup"}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                  >
                    {formToShow === "addGroup" ? (
                      <CheckIcon fontSize={"small"} />
                    ) : (
                      ""
                    )}{" "}
                    Group
                  </StyledToggleButton>
                )}
              />
            </StyledFormControl>
          </Box>
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            flexGrow={1}
          >
            <DialogActions>
              <PrimaryButton colors={colors}
                disabled={!formToShow}
                onClick={handleSubmit(onSubmit)}
              >
                Next
              </PrimaryButton>
            </DialogActions>
          </Box>
        </Box>
      </form>
    </>
  );
};

const SiteAndGroupNamingForm = ({
  onSubmit: onOverallSubmit,
  setOpen,
  dataConfig,
  setActiveStep,
  sitesAndGroupInfo,
  isPending: isAddSitesPending,
}) => {
  const { colors } = useTheme();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      siteSelected: "",
      groupName: "",
      siteName: "",
      siteLocation: "",
    },
  });
  const { whichForm } = dataConfig;
  const siteSelected = watch("siteSelected");
  const groupName = watch("groupName");
  const siteName = watch("siteName");
  const [authToken, setAuthToken] = useState("");
  const { getAccessTokenSilently } = useAuth0();

  const onSubmit = (data) => {
    onOverallSubmit(data);
  };

  React.useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const {
    data: siteLists,
    isLoading,
    isFetched,
  } = useGetSiteLists({ authToken });

  const siteNames =
    isFetched &&
    siteLists?.siteList.map((site) => site.siteName);

  console.log("sitesAndGroupInfo", sitesAndGroupInfo);

  return (
    <>
      <SiteAndGroupNamingFormHeader
        setOpen={setOpen}
        formReset={reset}
        siteConfig={dataConfig}
        setActiveStep={setActiveStep}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={6}
        >
          {whichForm === "addSite" ? (
            <>
              <StyledFormControl colors={colors} fullWidth margin="none">
                {/*<InputLabel>Status</InputLabel>*/}
                <Controller
                  name="siteName"
                  control={control}
                  defaultValue={""}
                  rules={{
                    maxLength: {
                      value: 512,
                      message:
                        "Site name must be 512 characters or fewer",
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <StyledTextField colors={colors}
                      {...field}
                      label="Name"
                      id="site-name-input"
                      style={{
                        flex: 1,
                        margin: 0,
                        minHeight: "2.5rem",
                        // padding: "16.5px 14px",
                      }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error
                          ? fieldState.error.message
                          : ""
                      }
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        field.onChange(trimmedValue);
                        field.onBlur();
                      }}
                    />
                  )}
                />
              </StyledFormControl>

              <StyledFormControl colors={colors} fullWidth margin="none">
                <Controller
                  name="siteLocation"
                  control={control}
                  defaultValue={""}
                  rules={{
                    required: "Site location is required",
                    maxLength: {
                      value: 512,
                      message:
                        "Site location must be 512 characters or fewer",
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <StyledTextField colors={colors}
                      {...field}
                      label="Add a site location"
                      id="site-location-input"
                      style={{
                        flex: 1,
                        margin: 0,
                        minHeight: "2.5rem",
                        // padding: "16.5px 14px",
                      }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error
                          ? fieldState.error.message
                          : ""
                      }
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        field.onChange(trimmedValue);
                        field.onBlur();
                      }}
                    />
                  )}
                />
              </StyledFormControl>
            </>
          ) : (
            <>
              {" "}
              <StyledFormControl
                colors={colors}
                fullWidth
                margin="none"
                error={!!errors.groupName}
              >
                <Controller
                  name="groupName"
                  control={control}
                  defaultValue={""}
                  rules={{
                    validate: (value) => {
                      const siteToUpdate =
                        sitesAndGroupInfo.find(
                          (site) =>
                            site.siteName.toLowerCase() ===
                            siteSelected.toLowerCase()
                        );
                      console.log(
                        "siteToUpdate",
                        siteToUpdate
                      );
                      const groupExists =
                        siteToUpdate &&
                        siteToUpdate?.groups.length > 0 &&
                        siteToUpdate?.groups.findIndex(
                          (group) => {
                            console.log("group", group);
                            console.log("value", value);
                            return (
                              group?.DGname?.toLowerCase() ===
                              value?.toLowerCase()
                            );
                          }
                        ) !== -1;
                      if (groupExists) {
                        return "Group already exists";
                      }
                      return true;
                    },
                    maxLength: {
                      value: 512,
                      message:
                        "Group name must be 512 characters or fewer",
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <StyledTextField colors={colors}
                      {...field}
                      label="Name"
                      id="site-name-input"
                      style={{
                        flex: 1,
                        margin: 0,
                        minHeight: "2.5rem",
                        // padding: "16.5px 14px",
                      }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error
                          ? fieldState.error.message
                          : ""
                      }
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        field.onChange(trimmedValue);
                        field.onBlur();
                      }}
                    />
                  )}
                />
              </StyledFormControl>
              <StyledFormControl
                colors={colors}
                fullWidth
                margin="none"
                // error={!!errors.selectedOption}
              >
                <InputLabel id="select-label">
                  Site
                </InputLabel>
                <Controller
                  name="siteSelected"
                  control={control}
                  // rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <StyledSelect
                      {...field}
                      label="Site"
                      colors={colors}
                      disabled={isLoading}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: "250px",
                            overflowY: "auto",
                            backgroundColor: colors.background.paper,
                            color: colors.text.primary,
                          },
                        },
                      }}
                      endAdornment={
                        isLoading && (
                          <CircularProgress
                            sx={{ color: colors.primary.main }}
                            size={20}
                          />
                        )
                      }
                    >
                      {siteNames.length > 0 &&
                        siteNames?.map((option, index) => (
                          <StyledMenuItem
                            colors={colors}
                            key={index}
                            value={option}
                          >
                            {option}
                          </StyledMenuItem>
                        ))}
                    </StyledSelect>
                  )}
                />
              </StyledFormControl>
            </>
          )}

          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            flexGrow={1}
          >
            <DialogActions>
              <PrimaryButton colors={colors}
                disabled={
                  (whichForm === "addGroup" &&
                    (!siteSelected || !groupName)) ||
                  (whichForm === "addSite" && !siteName)
                }
                endIcon={
                  isAddSitesPending && (
                    <CircularProgress
                      size={"0.85rem"}
                      sx={{ color: colors.primary.main }}
                    />
                  )
                }
                type="submit"
              >
                Submit
              </PrimaryButton>
            </DialogActions>
          </Box>
        </Box>
      </form>
    </>
  );
};

const AddSitesAndGroupsDialog = ({
  open,
  setOpen,
  sitesAndGroupInfo,
  setSiteAndGroups,
  refetch,
  role,
  siteLists,
}) => {
  const { colors } = useTheme();
  console.log("siteLists", siteLists?.length);
  const siteListsLength = siteLists?.length || 0;
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const [activeStep, setActiveStep] = React.useState(0);
  const [dataConfig, setDataConfig] = useState({});
  const [authToken, setAuthToken] = useState("");
  const { getAccessTokenSilently } = useAuth0();

  const totalSteps = 2;
  const onMoveForward = (data) => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setDataConfig((prevDataConfig) => ({
      ...prevDataConfig,
      ...data,
    }));
  };

  const { mutate: addSite } = usePostNewSite();
  const { mutate: addGroup } = usePostNewDG();

  const onMoveBackward = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const generateRandomSiteId = () => {
    return (
      "site-" + Math.random().toString(36).substr(2, 9)
    );
  };

  const generateRandomNumber = (min = 50, max = 100) => {
    return (
      Math.floor(Math.random() * (max - min + 1)) + min
    );
  };

  const onSubmit = (data) => {
    if (dataConfig?.whichForm === "addSite") {
      addSite(
        data.siteName ? data.siteName.trim() : "",
        data.siteLocation ? data.siteLocation.trim() : "",
        {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            setToastMessage(
              `Site ${data?.siteName} added successfully`
            );
            setToastOpen(true);
            await refetch();
            setActiveStep(0);
            setOpen(false);
          } else {
            setToastMessage(`Error: ${response?.msg}`);
            setToastOpen(true);
          }
        },
        onError: (error) => {
          console.error("Error adding site:", error);
          setToastMessage("Error adding site");
          setToastOpen(true);
        },
      });
      setActiveStep(0);
      setOpen(false);
      refetch();
    } else {
      console.log("data", data);
      addGroup(
        {
          siteName: data.siteSelected
            ? data.siteSelected.trim()
            : "",
          dgname: data.groupName
            ? data.groupName.trim()
            : "",
        },
        {
          onSuccess: async (response) => {
            if (response?.status === "ok") {
              setToastMessage(
                `Group ${data?.groupName} has been added to ${data?.siteSelected} successfully`
              );
              setToastOpen(true);
              await refetch();
              setActiveStep(0);
              setOpen(false);
            } else {
              setToastMessage(`Error: ${response?.msg}`);
              setToastOpen(true);
            }
          },
          onError: (error) => {
            console.error(
              "Error adding group to site:",
              error
            );
            setToastMessage("Error adding group to site");
            setToastOpen(true);
          },
        }
      );
      setActiveStep(0);
      setOpen(false);
      refetch();
    }
  };
  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={open}
      hideBackdrop
    >
      <DialogContent sx={{ padding: 0 }}>
        {activeStep === 0 && (
          <SiteOrGroupSelectionForm
            onMoveForward={onMoveForward}
            setOpen={setOpen}
            setActiveStep={setActiveStep}
            role={role}
            siteListsLength={siteListsLength}
          />
        )}
        {activeStep === 1 && (
          <SiteAndGroupNamingForm
            onSubmit={onSubmit}
            setOpen={setOpen}
            dataConfig={dataConfig}
            setActiveStep={setActiveStep}
            sitesAndGroupInfo={sitesAndGroupInfo}
          />
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default AddSitesAndGroupsDialog;
